import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { UserPlus, LogIn, Mail, Lock, User, Users, Eye, EyeOff } from 'lucide-react'
import toast from 'react-hot-toast'
import ApiService from '../services/SupabaseApiService'
import { TurnstileWidget, isTurnstileEnabled, requireCaptchaToken } from './TurnstileWidget'

const inputClassName =
  'w-full p-4 rounded-xl border-2 border-slate-300 bg-slate-50 text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-500/25 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-400 dark:focus:border-blue-400 dark:focus:bg-slate-900'

const labelClassName =
  'mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200'

const AuthScreen = ({ onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    parentName: '',
    familyName: '',
  })

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  // Fresh Turnstile token when switching Sign In / Sign Up
  useEffect(() => {
    setCaptchaToken('')
  }, [isLogin])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      requireCaptchaToken(captchaToken)

      let result
      if (isLogin) {
        result = await ApiService.login(formData.email, formData.password, captchaToken || null)
        toast.success(`Welcome back, ${result.user.parentName}! 👋`)
      } else {
        if (!formData.parentName || !formData.familyName) {
          toast.error('Please fill in all fields')
          setIsLoading(false)
          return
        }
        result = await ApiService.signup(
          formData.email,
          formData.password,
          formData.parentName,
          formData.familyName,
          captchaToken || null
        )

        if (result.needsConfirmation) {
          toast.success('Check your email to confirm this new account.')
          toast(`We sent a confirmation link to ${formData.email}`, { icon: '📧' })
          setIsLogin(true)
          setIsLoading(false)
          return
        }

        toast.success(`Welcome to TrophyTime, ${result.user.parentName}! 🎉`)
      }

      onAuthSuccess(result)
    } catch (error) {
      console.error('Authentication error:', error)
      setCaptchaToken('')
      const msg = error.message || 'Authentication failed'
      if (msg.includes('already exists') || msg.includes('reserved for platform')) {
        toast.error(msg)
        setIsLogin(true)
      } else if (/supabase|database|rest\/v1|postgres|rpc/i.test(msg)) {
        // Never surface backend/infrastructure details to users
        toast.error('Something went wrong. Please try again in a moment.')
      } else {
        toast.error(msg)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen app-shell flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <motion.div
          className="text-center mb-8"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md dark:border-slate-700 dark:bg-slate-800"
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="text-4xl" aria-hidden="true">🏆</span>
          </motion.div>
          <h1 className="mb-2 font-fredoka text-4xl app-text">TrophyTime</h1>
          <p className="font-nunito app-text-muted">
            Family task management made fun
          </p>
        </motion.div>

        <motion.div
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-900"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="mb-6 flex rounded-xl border border-slate-200 bg-slate-100 p-1 dark:border-slate-700 dark:bg-slate-800">
            <motion.button
              type="button"
              onClick={() => setIsLogin(true)}
              className={`flex-1 rounded-lg px-4 py-3 font-semibold transition-all ${
                isLogin
                  ? 'bg-white text-indigo-700 shadow-sm dark:bg-slate-700 dark:text-blue-300'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-pressed={isLogin}
            >
              <LogIn size={18} className="mr-2 inline" aria-hidden="true" />
              Sign In
            </motion.button>
            <motion.button
              type="button"
              onClick={() => setIsLogin(false)}
              className={`flex-1 rounded-lg px-4 py-3 font-semibold transition-all ${
                !isLogin
                  ? 'bg-white text-indigo-700 shadow-sm dark:bg-slate-700 dark:text-blue-300'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-pressed={!isLogin}
            >
              <UserPlus size={18} className="mr-2 inline" aria-hidden="true" />
              Sign Up
            </motion.button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <label className={labelClassName} htmlFor="parentName">
                    <User size={16} aria-hidden="true" />
                    Parent Name
                  </label>
                  <input
                    id="parentName"
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleInputChange}
                    className={inputClassName}
                    placeholder="Enter your name"
                    required={!isLogin}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <label className={labelClassName} htmlFor="familyName">
                    <Users size={16} aria-hidden="true" />
                    Family Name
                  </label>
                  <input
                    id="familyName"
                    type="text"
                    name="familyName"
                    value={formData.familyName}
                    onChange={handleInputChange}
                    className={inputClassName}
                    placeholder="e.g., The Smith Family"
                    required={!isLogin}
                  />
                </motion.div>
              </>
            )}

            <div>
              <label className={labelClassName} htmlFor="email">
                <Mail size={16} aria-hidden="true" />
                Email Address
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={inputClassName}
                placeholder="Enter your email"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label className={labelClassName} htmlFor="password">
                <Lock size={16} aria-hidden="true" />
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`${inputClassName} pr-12`}
                  placeholder="Enter your password"
                  required
                  minLength={6}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition-colors hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {!isLogin && (
                <motion.p
                  className="mt-2 text-sm text-slate-500 dark:text-slate-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  Password must be at least 6 characters long
                </motion.p>
              )}
            </div>

            {isTurnstileEnabled() && (
              <TurnstileWidget
                key={isLogin ? 'login' : 'signup'}
                onToken={setCaptchaToken}
              />
            )}

            <motion.button
              type="submit"
              disabled={isLoading || (isTurnstileEnabled() && !captchaToken)}
              className="flex w-full transform items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 font-bold text-white transition-all duration-300 hover:from-indigo-700 hover:to-blue-700 hover:scale-105 disabled:cursor-not-allowed disabled:scale-100 disabled:from-slate-400 disabled:to-slate-500"
              whileHover={{ scale: isLoading ? 1 : 1.05 }}
              whileTap={{ scale: isLoading ? 1 : 0.95 }}
            >
              {isLoading ? (
                <motion.div
                  className="h-6 w-6 rounded-full border-2 border-white border-t-transparent"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
              ) : (
                <>
                  {isLogin ? <LogIn size={20} className="mr-2" /> : <UserPlus size={20} className="mr-2" />}
                  {isLogin ? 'Sign In' : 'Create Account'}
                </>
              )}
            </motion.button>
          </form>

          <motion.div
            className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p className="mb-2">
              {isLogin
                ? "Don't have an account? Click Sign Up above!"
                : 'Already have an account? Click Sign In above!'}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-500">
              🔒 Your data is stored securely {!isLogin && 'and can be backed up to the cloud'}
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          className="mt-8 text-center text-sm text-slate-500 dark:text-slate-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          Made with ❤️ for families everywhere
        </motion.div>
      </div>
    </div>
  )
}

export default AuthScreen
